// src/components/GildarcLearnLanding.js

import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  BookOpenCheck,
  ChevronDown,
  Shield,
  BadgeDollarSign,
} from "lucide-react";
import { Link } from "react-router-dom";
import StoriesAccordion from "../Jordan/StoriesAccordion";
import Header from "../Header/Header";
import MovingTicker from "../Header/MovingTicker";
import { useTranslation } from "react-i18next";
import NowVsFutureComparison from "../Phone/NowVsFutureComparison";
import NowVsFutureSection from "../Phone/NowVsFutureSection";
import Section1 from "../Page1Main/Sections/Section1";
import { useNavigate } from "react-router-dom";
import SummaryNextSteps from "./SummaryNextSteps";

const MODULES = [
  {
    id: "A",
    title: "mod-A-title",
    stories: 4,
    summary: "mod-A-summary",
  },
  {
    id: "B",
    title: "mod-B-title",
    stories: 11,
    summary: "mod-B-summary",
    liveNowLabel: "mod-B-liveNowLabel",
    storiesList: [
      {
        id: "B1",
        title: "mod-B1-title",
        path: "/saver",
        status: "available",
      },
      {
        id: "B2",
        title: "mod-B2-title",
        path: "/saver",
        status: "coming-soon",
      },
      {
        id: "B3",
        title: "mod-B3-title",
        path: "/saver",
        status: "coming-soon",
      },
      {
        id: "B4",
        title: "mod-B4-title",
        path: "/saver",
        status: "coming-soon",
      },
      {
        id: "B5",
        title: "mod-B5-title",
        path: "/saver",
        status: "coming-soon",
      },
      {
        id: "B6",
        title: "mod-B6-title",
        path: "/saver",
        status: "coming-soon",
      },
      {
        id: "B7",
        title: "mod-B7-title",
        path: "/saver",
        status: "coming-soon",
      },
      {
        id: "B8",
        title: "mod-B8-title",
        path: "/saver",
        status: "coming-soon",
      },
      {
        id: "B9",
        title: "mod-B9-title",
        path: "/saver",
        status: "coming-soon",
      },
      {
        id: "B10",
        title: "mod-B10-title",
        path: "/saver",
        status: "coming-soon",
      },
      {
        id: "B11",
        title: "mod-B11-title",
        path: "/saver",
        status: "coming-soon",
      },
    ],
  },
  {
    id: "C",
    title: "mod-C-title",
    stories: 8,
    summary: "mod-C-summary",
  },
  {
    id: "D",
    title: "mod-D-title",
    stories: 8,
    summary: "mod-D-summary",
  },
  {
    id: "E",
    title: "mod-E-title",
    stories: 11,
    summary: "mod-E-summary",
  },
  {
    id: "F",
    title: "mod-F-title",
    stories: 3,
    summary: "mod-F-summary",
  },
  {
    id: "G",
    title: "mod-G-title",
    stories: 8,
    summary: "mod-G-summary",
  },
  {
    id: "H",
    title: "mod-H-title",
    stories: 7,
    summary: "mod-H-summary",
  },
  {
    id: "I",
    title: "mod-I-title",
    stories: 8,
    summary: "mod-I-summary",
  },
  {
    id: "J",
    title: "mod-J-title",
    stories: 5,
    summary: "mod-J-summary",
  },
  {
    id: "K",
    title: "mod-K-title",
    stories: 11,
    summary: "mod-K-summary",
  },
  {
    id: "L",
    title: "mod-L-title",
    stories: 5,
    summary: "mod-L-summary",
  },
];

const AccordionItem = ({ module, isOpen, onToggle, isLocked }) => {
  const { t } = useTranslation();

  return (
    <div
      className={`rounded-2xl overflow-hidden shadow-sm border
        ${
          isLocked
            ? "bg-brown-100 border-slate-300 opacity-70"
            : "bg-white border-slate-200"
        }
      `}
    >
      <button
        type="button"
        className={`flex w-full items-center justify-between px-4 sm:px-6 py-4 text-left ${
          isLocked ? "cursor-default" : "hover:bg-slate-50 cursor-pointer"
        }`}
        onClick={isLocked ? undefined : onToggle}
        disabled={isLocked}
      >
        <div className="flex items-center gap-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-100 text-sm font-semibold text-slate-900">
            {module.id}
          </div>
          <div>
            <p className="font-semibold text-slate-900 text-sm sm:text-base">
              {t(module.title)}
            </p>
            <p className="text-xs text-slate-500 sm:text-sm flex items-center gap-2">
              {module.stories} {t("mod-stories-label", "stories")}
              {module.liveNowLabel && (
                <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[10px] font-semibold">
                  {t(module.liveNowLabel)}
                </span>
              )}
            </p>
          </div>
        </div>
        {!isLocked && (
          <ChevronDown
            className={`h-5 w-5 text-slate-500 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        )}
      </button>

      {/* Only unlocked modules can open */}
      {!isLocked && isOpen && (
        <div
          className="border-t border-slate-100 bg-slate-50 px-4 sm:px-6 py-4 text-sm text-slate-700 font-bold"
          style={{ textAlign: "left" }}
        >
          {t(module.summary)}

          {module.storiesList && (
            <ul className="mt-4 space-y-2">
              {module.storiesList.map((story) => (
                <li key={story.id}>
                  <Link
                    to={story.path}
                    className={`flex items-center justify-between rounded-xl px-3 py-2 text-xs sm:text-sm border
  ${
    story.status === "available"
      ? "bg-emerald-50 border-emerald-300 text-emerald-900 hover:bg-emerald-100"
      : story.status === "completed"
      ? "bg-sky-50 border-sky-300 text-sky-900 hover:bg-sky-100"
      : story.status === "coming-soon"
      ? "bg-gray-50 border-gray-200 text-gray-700 cursor-not-allowed"
      : "bg-white border-slate-200 text-slate-500"
  }
`}
                  >
                    <span>
                      <span className="font-semibold mr-2">{story.id}.</span>
                      {t(story.title)}
                    </span>
                   {story.status === "available" && (
  <span className="text-slate-400 text-lg leading-none">+</span>
)}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default function GildarcLearnHome() {
const navigate = useNavigate();
  const { t } = useTranslation();
  // default open B
  const [openId, setOpenId] = useState("B");

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-100 via-orange-50 to-slate-50 text-slate-900">
 


      <div className="mx-auto flex max-w-5xl flex-col gap-8 px-4 py-10 sm:px-6 lg:py-14">
        {/* Hero / intro */}
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl bg-white/80 p-5 sm:p-8 shadow-lg border border-orange-200"
        >
          <div
  style={{
    textAlign: "center",
    maxWidth: "860px",
    margin: "0 auto",
  }}
>
  {/* Badge */}
  <div
    style={{
      marginBottom: "14px",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      borderRadius: "999px",
      background: "#ffedd5",
      padding: "6px 12px",
      fontSize: "12px",
      fontWeight: 600,
      color: "#9a3412",
      border: "1px solid rgba(251, 146, 60, 0.25)",
    }}
  >
    <Sparkles style={{ width: "16px", height: "16px" }} />
    <span>Welcome to your Samarc learning module</span>
  </div>

  {/* Headline */}
  <h1
    style={{
      fontSize: "clamp(28px, 4.5vw, 44px)",
      lineHeight: 1.08,
      fontWeight: 800,
      color: "#0f172a",
      margin: "0 0 10px 0",
      letterSpacing: "-0.02em",
    }}
  >
    {t("mod-heading")}
  </h1>

  {/* Subheading */}
  <p
    style={{
      margin: "0 auto",
      maxWidth: "720px",
      fontSize: "15px",
      lineHeight: 1.65,
      color: "#334155",
    }}
  >
    {t("mod-subheading")}
  </p>

  {/* Reveal section (left-aligned) */}
  <div
    style={{
      marginTop: "18px",
      textAlign: "left",
      background: "rgba(255, 237, 213, 0.55)",
      border: "1px solid rgba(251, 146, 60, 0.20)",
      borderRadius: "16px",
      padding: "14px 14px",
    }}
  >
    {/* Reveal label */}
    <div
      style={{
        fontSize: "13px",
        fontWeight: 800,
        color: "#0f172a",
        marginBottom: "6px",
      }}
    >
      {t("mod-noteS")}
    </div>

    {/* Reveal body */}
    <p
      style={{
        margin: 0,
        fontSize: "13px",
        lineHeight: 1.6,
        color: "#64748b",
      }}
    >
      {t("mod-note")}
    </p>

    {/* Small guardrail line (optional but recommended for trust) */}
    <p
      style={{
        margin: "10px 0 0 0",
        fontSize: "11px",
        lineHeight: 1.5,
        color: "#94a3b8",
      }}
    >
      Education-only. “Up to” and eligibility rules apply - Form 8880 official IRS instructions.
    </p>
  </div>
</div>

        
        </motion.section>


           <div style={{ marginBottom: "-10px", marginTop: "50px"  }}>
              <NowVsFutureSection />
            </div>






<SummaryNextSteps/>

























          





      </div>

      {/* Jordan stories accordion or other content below */}
      {/* <StoriesAccordion /> */}

    
    </div>
  );
}
